import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, Package } from "lucide-react";
import { AuthForm } from "../components/AuthForm";
import { fetchMyOrders, cancelSubscription } from "../lib/api";
import { money, useShop } from "../context/ShopContext";
import { toast } from "sonner";

const SubscriptionCard = ({ o, token, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const active = o.subscription_status === "active";

  const cancel = async () => {
    if (!window.confirm("Cancel your monthly refill plan? You won't be billed again.")) return;
    setBusy(true);
    try {
      await cancelSubscription(o.id, token);
      toast.success("Your refill plan has been cancelled.");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "We couldn't cancel that. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-gold bg-[#fbf8f1] p-7" data-testid={`subscription-${o.order_number}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="eyebrow mb-3">Refill plan</p>
          <p className="font-serif text-xl text-ink">{o.items[0]?.name}</p>
          {o.items[0]?.scents?.length > 0 && (
            <p className="text-xs text-ink-muted mt-1">{o.items[0].scents.join(" · ")}</p>
          )}
        </div>
        <div className="text-right">
          <p className="font-serif text-2xl text-ink">{money(o.total)}<span className="text-sm text-ink-muted"> /month</span></p>
          <p className="text-[0.6rem] uppercase tracking-[0.18em] text-ink-muted mt-1">
            {active ? "Active" : "Cancelled"}
          </p>
        </div>
      </div>
      {active && (
        <button onClick={cancel} disabled={busy} data-testid={`cancel-subscription-${o.order_number}`}
          className="mt-6 inline-flex items-center gap-2 px-5 py-3 border border-cream-border text-ink text-xs uppercase tracking-[0.15em] hover:border-red-500 hover:text-red-600 transition-colors disabled:opacity-60">
          {busy ? "Cancelling…" : "Cancel plan"}
        </button>
      )}
    </div>
  );
};

const statusTone = {
  pending: "text-ink-muted border-cream-border",
  paid: "text-gold border-gold",
  fulfilled: "text-emerald-700 border-emerald-700/40",
  cancelled: "text-red-600 border-red-600/40",
  refunded: "text-ink-muted border-cream-border",
};

const OrderCard = ({ o }) => (
  <div className="border border-cream-border bg-[#fbf8f1] p-7" data-testid={`order-${o.order_number}`}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p className="font-serif text-xl text-ink">{o.order_number}</p>
        <p className="text-xs text-ink-muted mt-1">{new Date(o.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</p>
      </div>
      <span className={`text-[0.6rem] uppercase tracking-[0.18em] px-3 py-1.5 border ${statusTone[o.status] || statusTone.pending}`}>
        {o.status}
      </span>
    </div>
    <div className="mt-6 space-y-3 border-t border-cream-border pt-5">
      {o.items.map((it, i) => (
        <div key={i} className="flex gap-4">
          <img src={it.image} alt="" className="w-12 h-12 object-cover bg-cream-alt shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-ink">{it.name} <span className="text-ink-muted">× {it.quantity}</span></p>
            {it.scents?.length > 0 && <p className="text-[0.65rem] text-ink-muted mt-1">{it.scents.join(" · ")}</p>}
          </div>
          <span className="text-sm text-ink shrink-0">{money(it.price * it.quantity)}</span>
        </div>
      ))}
    </div>
    <div className="flex justify-between items-baseline mt-5 pt-5 border-t border-cream-border">
      <span className="text-[0.65rem] uppercase tracking-[0.18em] text-ink-muted">
        Total {o.delivery_fee > 0 && `(incl. ${money(o.delivery_fee)} delivery)`}
      </span>
      <span className="font-serif text-xl text-ink">{money(o.total)}</span>
    </div>
    {o.tracking && <p className="text-xs text-ink-muted mt-4">Tracking: <span className="text-ink">{o.tracking}</span></p>}
  </div>
);

export default function Account() {
  const { customer, token, signOut } = useShop();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    fetchMyOrders(token).then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const subs = orders.filter((o) => o.is_subscription && o.payment_status === "paid");
  const purchases = orders.filter((o) => !o.is_subscription);

  return (
    <div className="bg-cream min-h-[80vh]" data-testid="account-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-24">
        {!customer ? (
          <div className="max-w-xl">
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">Your account</h1>
            <span className="block w-14 h-px bg-gold mt-5 mb-10" />
            <AuthForm heading="Sign in or create an account" blurb="Track your orders and reorder refills without re-entering your details." />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="eyebrow mb-4">Account</p>
                <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">{customer.name || "Welcome"}</h1>
                <p className="text-ink-muted mt-3">{customer.email}</p>
              </div>
              <button onClick={signOut} data-testid="account-signout"
                className="inline-flex items-center gap-2 px-5 py-3 border border-cream-border text-ink text-xs uppercase tracking-[0.15em] hover:border-gold hover:text-gold transition-colors">
                <LogOut size={14} /> Sign out
              </button>
            </div>

            {subs.length > 0 && (
              <>
                <h2 className="font-serif text-2xl text-ink mt-16 mb-7">Your refill plan</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="subscriptions-list">
                  {subs.map((o) => <SubscriptionCard key={o.id} o={o} token={token} onChanged={load} />)}
                </div>
              </>
            )}

            <h2 className="font-serif text-2xl text-ink mt-16 mb-7">Order history</h2>
            {loading ? (
              <p className="text-ink-muted">Loading your orders…</p>
            ) : purchases.length === 0 ? (
              <div className="border border-cream-border bg-[#fbf8f1] p-14 text-center" data-testid="no-orders">
                <Package size={30} strokeWidth={1.2} className="text-gold mx-auto mb-6" />
                <p className="text-ink-muted">No orders yet.</p>
                <Link to="/shop" className="inline-block mt-6 text-gold text-xs uppercase tracking-[0.2em]">Browse the shop</Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="orders-list">
                {purchases.map((o) => <OrderCard key={o.id} o={o} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
